/**
 * PLA-33 (R01-06): OTP Verification Component
 * Displays OTP input with resend functionality for campaign email verification.
 */

import { useState, useEffect, useCallback } from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { sendOtp, verifyOtp, type OtpVerifyResponse } from "@/lib/campaign/api";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface OtpVerificationProps {
  campaignSessionToken: string;
  onSuccess?: () => void;
}

const RESEND_COOLDOWN_SECONDS = 30;
const MAX_RESEND_ATTEMPTS = 3;

export function OtpVerification({ campaignSessionToken, onSuccess }: OtpVerificationProps) {
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const navigate = useNavigate();

  const sendOtpCode = useCallback(async () => {
    if (isSending) return;
    if (resendAttempts >= MAX_RESEND_ATTEMPTS) {
      setError("Maximum resend attempts reached. Please try again later.");
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const result = await sendOtp({ campaign_session_token: campaignSessionToken });
      if (result.ok) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setResendAttempts((prev) => prev + 1);
      } else {
        setError(result.error || "Failed to send verification code. Please try again.");
      }
    } catch (err) {
      setError("Failed to send verification code. Please try again.");
      console.error("[OtpVerification] Send error:", err);
    } finally {
      setIsSending(false);
    }
  }, [campaignSessionToken, isSending, resendAttempts]);

  // Auto-send OTP on mount
  useEffect(() => {
    sendOtpCode();
  }, [sendOtpCode]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError("Please enter the full 6-digit code");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const result: OtpVerifyResponse = await verifyOtp({
        campaign_session_token: campaignSessionToken,
        code: otp,
      });

      if (result.ok && result.session_token && result.refresh_token) {
        // Set Supabase auth session
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.session_token,
          refresh_token: result.refresh_token,
        });

        if (sessionError) {
          throw new Error("Failed to set session");
        }

        // Call success callback or redirect
        if (onSuccess) {
          onSuccess();
        } else {
          // Default redirect to home
          navigate("/home");
        }
      } else {
        setError(result.error || "Invalid verification code. Please try again.");
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
      console.error("[OtpVerification] Verify error:", err);
    } finally {
      setIsVerifying(false);
    }
  };

  const canResend = resendCooldown === 0 && resendAttempts < MAX_RESEND_ATTEMPTS && !isSending;

  return (
    <div className="flex flex-col items-center gap-6 p-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Verify Your Email</h2>
        <p className="text-muted-foreground">
          We sent a 6-digit code to your email. Enter it below to continue.
        </p>
      </div>

      <div className="flex flex-col items-center gap-4">
        <InputOTP
          maxLength={6}
          value={otp}
          onChange={(value) => {
            setOtp(value);
            setError(null);
          }}
          onComplete={handleVerify}
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        {error && (
          <p className="text-sm text-destructive text-center max-w-md">{error}</p>
        )}

        <Button
          onClick={handleVerify}
          disabled={isVerifying || otp.length !== 6}
          className="w-full"
        >
          {isVerifying ? "Verifying..." : "Verify Code"}
        </Button>

        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-muted-foreground">
            Didn't receive a code?
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={sendOtpCode}
            disabled={!canResend}
            className="text-sm"
          >
            {isSending
              ? "Sending..."
              : resendCooldown > 0
              ? `Resend code (${resendCooldown}s)`
              : resendAttempts >= MAX_RESEND_ATTEMPTS
              ? "Maximum resends reached"
              : "Resend code"}
          </Button>
        </div>
      </div>
    </div>
  );
}

